/*! Copyright (c) Safe As Milk. All rights reserved. */
import DetailsDisclosure from "details-disclosure";

class HeaderDetailsDisclosure extends DetailsDisclosure {
    static #pendingCloseTimeout;
    static #pendingCloseDisclosure;
    static #CLOSE_DELAY_MS = 150;
    #boundMouseEnterListener;
    #boundMouseLeaveListener;
    #summaryLink;
    constructor() {
        super();
        this.#boundMouseEnterListener = this.mouseEnterListener.bind(this);
        this.#boundMouseLeaveListener = this.mouseLeaveListener.bind(this);
    }
    connectedCallback() {
        this.#summaryLink = this.querySelector("summary > a");
        if (!this.mainDetailsToggle.hasAttribute("open")) this.#disableSummaryLink();
        this.mainDetailsToggle.addEventListener("mouseenter", this.#boundMouseEnterListener);
        this.mainDetailsToggle.addEventListener("mouseleave", this.#boundMouseLeaveListener);
    }
    disconnectedCallback() {
        this.disableListeners();
        if (HeaderDetailsDisclosure.#pendingCloseDisclosure === this) {
            HeaderDetailsDisclosure.#clearPendingClose();
        }
    }
    open() {
        if (this.mainDetailsToggle.hasAttribute("open")) return;

        const otherOpenMenus = Array.from(document.querySelectorAll("header-details-disclosure")).filter((disclosure) => {
            return disclosure !== this && disclosure.mainDetailsToggle?.hasAttribute("open");
        });
        const switchingMegaMenus = otherOpenMenus.length > 0 && this.classList.contains("mega");

        if (switchingMegaMenus) {
            this.content.classList.add("is-instant-open");
        } else {
            this.content.classList.remove("is-instant-open");
        }

        if (this.classList.contains("mega")) {
            this.content.classList.add("is-topmost");
            otherOpenMenus.forEach((disclosure) => disclosure.content.classList.remove("is-topmost"));
        }

        this.mainDetailsToggle.setAttribute("open", "");
        this.querySelector("summary").setAttribute("aria-expanded", true);
        if (this.content.getBoundingClientRect().x + this.content.offsetWidth > window.innerWidth - 30) {
            this.content.classList.add("is-left-aligned");
        }
        if (this.hasAttribute("adjust-mega-menu-height")) this.#setContentHeight(36);

        if (switchingMegaMenus) {
            requestAnimationFrame(() => {
                otherOpenMenus.forEach((disclosure) => disclosure.close());
            });
        } else {
            otherOpenMenus.forEach((disclosure) => disclosure.close());
        }
    }
    close() {
        if (!this.mainDetailsToggle.hasAttribute("open")) return;
        this.mainDetailsToggle.removeAttribute("open");
        this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
        this.content.classList.remove("is-left-aligned");
        this.content.classList.remove("is-instant-open");
        this.content.classList.remove("is-topmost");
        if (this.hasAttribute("adjust-mega-menu-height")) this.#removeContentHeight();
    }
    onToggle() {
        // Cache animation instances once and replay them — cancel() + play() on the same
        // WAAPI objects is required for content to reappear on subsequent opens.
        if (!this.panelAnimations) this.panelAnimations = this.content.getAnimations();
        if (!this.contentAnimations) {
            this.contentAnimations = Array.from(this.querySelectorAll(".has-animation")).reduce(((animations, element) => {
                const animation = element.getAnimations();
                return [ ...animations, ...animation ];
            }), []);
        }
        if (this.mainDetailsToggle.hasAttribute("open")) {
            setTimeout((() => {
                this.#enableSummaryLink();
            }), 50);

            if (this.content.classList.contains("is-instant-open")) {
                // Skip replaying a stale panel fade (starts at opacity 0). CSS keeps the shell opaque.
                this.panelAnimations.forEach((animation => animation.cancel()));
            } else {
                // Empty cache can happen if the first open was an instant switch (animation: none).
                if (!this.panelAnimations.length) {
                    this.panelAnimations = this.content.getAnimations();
                }
                this.panelAnimations.forEach((animation => animation.play()));
            }

            // Refresh content animation list if the first open captured none (e.g. instant timing).
            if (!this.contentAnimations.length) {
                this.contentAnimations = Array.from(this.querySelectorAll(".has-animation")).reduce(((animations, element) => {
                    const animation = element.getAnimations();
                    return [ ...animations, ...animation ];
                }), []);
            }
            this.contentAnimations.forEach((animation => animation.play()));

            document.body.setAttribute("header-menu-open", "");
            if (this.classList.contains("mega")) {
                document.body.setAttribute("header-mega-menu-open", "");
            }
        } else {
            this.#disableSummaryLink();
            this.panelAnimations.forEach((animation => animation.cancel()));
            this.contentAnimations.forEach((animation => animation.cancel()));

            const anotherOpen = document.querySelector("header-details-disclosure details[open]");
            if (!anotherOpen) {
                document.body.removeAttribute("header-menu-open");
                document.body.removeAttribute("header-mega-menu-open");
            } else if (!anotherOpen.closest("header-details-disclosure")?.classList.contains("mega")) {
                document.body.removeAttribute("header-mega-menu-open");
            }
        }
    }
    mouseEnterListener() {
        if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;
        HeaderDetailsDisclosure.#clearPendingClose();
        this.open();
    }
    mouseLeaveListener() {
        if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;
        HeaderDetailsDisclosure.#clearPendingClose();
        HeaderDetailsDisclosure.#pendingCloseDisclosure = this;
        HeaderDetailsDisclosure.#pendingCloseTimeout = setTimeout(() => {
            HeaderDetailsDisclosure.#pendingCloseDisclosure?.close();
            HeaderDetailsDisclosure.#clearPendingClose();
        }, HeaderDetailsDisclosure.#CLOSE_DELAY_MS);
    }
    disableListeners() {
        this.mainDetailsToggle.removeEventListener("mouseenter", this.#boundMouseEnterListener);
        this.mainDetailsToggle.removeEventListener("mouseleave", this.#boundMouseLeaveListener);
    }
    static #clearPendingClose() {
        clearTimeout(HeaderDetailsDisclosure.#pendingCloseTimeout);
        HeaderDetailsDisclosure.#pendingCloseTimeout = null;
        HeaderDetailsDisclosure.#pendingCloseDisclosure = null;
    }
    #setContentHeight(padding = 0) {
        const headerSection = document.querySelector(".js-header");
        const headerBottomBoundary = headerSection && headerSection.getBoundingClientRect().bottom > 0 ? headerSection.getBoundingClientRect().bottom : 0;
        const combinedPanelCutoff = Math.round(headerBottomBoundary + padding);
        if (this.content.offsetHeight > window.innerHeight - combinedPanelCutoff) {
            this.content.style.setProperty("--header-elements-height", `${combinedPanelCutoff}px`);
            this.content.classList.add("has-height-control");
        }
    }
    #removeContentHeight() {
        this.content.classList.remove("has-height-control");
    }
    #enableSummaryLink() {
        if (this.#summaryLink) {
            this.#summaryLink.removeEventListener("click", HeaderDetailsDisclosure.#summaryLinkListener);
        }
    }
    #disableSummaryLink() {
        if (this.#summaryLink) {
            this.#summaryLink.addEventListener("click", HeaderDetailsDisclosure.#summaryLinkListener);
        }
    }
    static #summaryLinkListener(e) {
        e.preventDefault();
    }
}

customElements.define("header-details-disclosure", HeaderDetailsDisclosure);
//# sourceMappingURL=header-details-disclosure.js.map
